import { useMemo } from 'react';

import { Table } from '@tanstack/react-table';

import { TableFilterState } from '@/components/data-table/filters-v2';
import { useDataTable } from '@/hooks/use-data-table';
import { ApiError } from '@/modules/common/api/errors';

import { getTimeOffApprovalsTableColumns } from '../components/approvals-table/time-off-approvals-table-columns';
import { TIME_OFF_APPROVALS_TABLE_ID } from '../constants/time-off-approvals-filter-keys';
import { TimeOffRequest } from '../types/time-off-request';
import { useTimeOffRequestsToReview } from './use-time-off-requests-to-review';

interface UseTimeOffApprovalsTableResult {
  table: Table<TimeOffRequest>;
  isLoading: boolean;
  hasMore: boolean;
  loadMore: () => void;
  /** Resolved error message (i18n) or undefined. */
  errorMessage?: string;
  saveColumnSizing: () => void;
}

/**
 * Wires the to-review requests query to a TanStack table instance. Mirrors
 * {@link useTimeOffsTable}; column visibility + sizing persist to localStorage
 * via `useDataTable` (keyed on `TIME_OFF_APPROVALS_TABLE_ID`).
 */
export const useTimeOffApprovalsTable = (
  filters: TableFilterState,
  resolveErrorCode: (code: string) => string
): UseTimeOffApprovalsTableResult => {
  const { data, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage, error } =
    useTimeOffRequestsToReview(filters);

  const rows = useMemo<TimeOffRequest[]>(() => data?.items ?? [], [data]);
  const columns = useMemo(() => getTimeOffApprovalsTableColumns(), []);

  const { table, saveColumnSizing } = useDataTable<TimeOffRequest>({
    data: rows,
    columns,
    getRowId: row => String(row.id),
    tableId: TIME_OFF_APPROVALS_TABLE_ID,
    // Pin the Employee column so it stays visible while scrolling horizontally
    // (same pattern as the team-members table).
    initialState: {
      columnPinning: { left: ['employee'] },
    },
  });

  const errorMessage = error
    ? error instanceof ApiError
      ? resolveErrorCode(error.code)
      : error.message
    : undefined;

  return {
    table,
    isLoading: isLoading || isFetchingNextPage,
    hasMore: hasNextPage,
    loadMore: fetchNextPage,
    errorMessage,
    saveColumnSizing,
  };
};
